import React, { useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';
import Modal from './Modal';
import getCroppedImg from './getCroppedImg';
import { useTranslation } from '../LanguageContext';

/**
 * Crop-before-upload sheet — the same move/zoom the profile photo has always
 * had, pulled out of `Profile.jsx` so every other upload can use it too.
 *
 * The problem it solves is not vanity: every surface that shows an uploaded
 * image frames it with `object-cover`, so a photo whose shape does not match
 * the frame is silently centre-cropped by the browser. The uploader never sees
 * that happen and finds out from the finished card. Here the frame *is* the
 * crop, so what is inside it is what ships.
 *
 * Takes a `File` and hands back a cropped `File` — callers keep their own
 * upload path (Cloudflare, base64, whatever) rather than this knowing about
 * any of them. `busy` keeps the sheet up with its buttons disabled while that
 * upload runs, so the image on screen is still the one being sent.
 */

const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result));
    reader.addEventListener('error', reject);
    reader.readAsDataURL(file);
});

function ImageCropModal({
    file,
    aspect = 16 / 9,
    cropShape = 'rect',
    title,
    hint,
    counter = null,
    cancelLabel,
    busy = false,
    // Yükleme/kaydetme hatası. Burada gösteriliyor, alert ile değil: pencere
    // açık kaldığı için aynı kare tekrar denenebiliyor — hata yüzünden
    // kapanmak, kullanıcıya fotoğrafı baştan seçtirip yeniden kırptırıyordu.
    error = null,
    onCancel,
    onCropped,
}) {
    const { t } = useTranslation();
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [area, setArea] = useState(null);
    const [failed, setFailed] = useState(false);

    // Every new file starts from a clean frame — carrying the previous zoom
    // over would drop the next photo into a crop chosen for a different one.
    useEffect(() => {
        let cancelled = false;
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setArea(null);
        setFailed(false);
        if (!file) return undefined;

        (async () => {
            let source = null;
            try {
                const compressed = await imageCompression(file, {
                    maxSizeMB: 1.5, maxWidthOrHeight: 1920, useWebWorker: true,
                });
                source = await readFile(compressed);
            } catch (err) {
                // Compression is an optimisation, not the point. A file it
                // chokes on is still a file the user picked.
                try { source = await readFile(file); } catch (e) { source = null; }
            }
            if (cancelled) return;
            if (source) setImageSrc(source); else setFailed(true);
        })();

        return () => { cancelled = true; };
    }, [file]);

    const confirm = async () => {
        if (!imageSrc || !area) return;
        let url;
        try {
            url = await getCroppedImg(imageSrc, area);
            const blob = await (await fetch(url)).blob();
            const base = (file?.name || 'image').replace(/\.[^.]+$/, '');
            onCropped(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
        } catch (err) {
            setFailed(true);
        } finally {
            if (url) URL.revokeObjectURL(url);
        }
    };

    return (
        <Modal
            open={Boolean(file)}
            onClose={busy ? undefined : onCancel}
            title={title || t('imageCrop.title')}
            subtitle={hint || t('imageCrop.hint')}
            size="xl"
            // Dragging a crop handle regularly ends outside the panel, and
            // losing the photo to that is infuriating.
            closeOnBackdrop={false}
            headerAccessory={counter && (
                <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-500 text-[11px] font-bold flex-shrink-0">
                    {counter}
                </span>
            )}
            footer={
                <div className="flex gap-3">
                    <button type="button" className="btn-secondary flex-1 py-2.5 text-sm"
                        disabled={busy} onClick={onCancel}>
                        {cancelLabel || t('imageCrop.cancel')}
                    </button>
                    <button type="button" className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                        disabled={busy || !imageSrc || !area} onClick={confirm}>
                        {busy && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                        {busy ? t('imageCrop.uploading') : t('imageCrop.save')}
                    </button>
                </div>
            }
        >
            <div className="relative w-full overflow-hidden rounded-2xl bg-slate-900"
                style={{ aspectRatio: String(aspect), maxHeight: '46vh' }}>
                {imageSrc ? (
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        cropShape={cropShape}
                        showGrid={false}
                        restrictPosition
                        onCropChange={setCrop}
                        onCropComplete={(_, pixels) => setArea(pixels)}
                        onZoomChange={setZoom}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-slate-400">
                        {failed ? t('imageCrop.error') : t('imageCrop.preparing')}
                    </div>
                )}
            </div>

            {error && (
                <p className="mt-3 px-3 py-2 rounded-xl bg-red-50 border border-red-100 text-red-600 text-xs font-semibold">
                    {error}
                </p>
            )}

            <div className="mt-4">
                <label className="label">{t('imageCrop.zoom')}</label>
                <input type="range" min={1} max={3} step={0.05} value={zoom}
                    disabled={!imageSrc}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full accent-primary-600 disabled:opacity-40" />
            </div>
        </Modal>
    );
}

export default ImageCropModal;
